import { useEffect, useRef, useState } from "react";
import { Button, Card, Message, Upload } from "@arco-design/web-react";
import axios, { AxiosResponse } from "axios";
import classNames from "classnames";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { useTranslation } from "react-i18next";

import { endpoints } from "../../../../api/endpoints";
import { useInvalidatePublishedBanner } from "../../../../api/hooks/usePublishedBanner";
import { resolveBannerUrl } from "../../../../utils/bannerUrl";
import { imageFileFromUrl } from "../../../../utils/imageFileFromUrl";
import { ReactComponent as EditIcon } from "../../../../assets/icons/edit.svg";
import { Banner } from "../../../../models/Banners";
import { cachedToken } from "../../../../setupAxios";
import { useBreakpoint } from "../../../../utils/hooks";
import { EventImageCropDialog } from "../../../../components/EventImageCropDialog/EventImageCropDialog";

import "./AdminBanner.css";
import { currentTheme, currentThemeName } from "../../../../theme";

const BANNER_IMAGE_TYPES = ["image/jpeg", "image/png"];
/** Matches dashboard hero (~full width, ~40–52vh tall). */
const BANNER_CROP_ASPECT = 3;

type AdminBannerProps = {
  /** legacy = white card frame on admin panel. hero = full-bleed preview dashboard only. */
  variant?: "legacy" | "hero";
};

function getErrorMessage(error: unknown, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data;
    if (typeof data === "string" && data.trim()) return data;
    if (data && typeof data === "object" && "message" in data) {
      const message = (data as { message?: string }).message;
      if (message?.trim()) return message;
    }
  }
  return fallback;
}

export function AdminBanner({ variant = "legacy" }: AdminBannerProps) {
  const { t } = useTranslation();
  const { isMobile } = useBreakpoint("mobile");
  const invalidatePublishedBanner = useInvalidatePublishedBanner();
  /** Crop/reposition only on Figma preview home (`variant="hero"`). Legacy `/admin/dashboard` uploads directly. */
  const useBannerCropFlow = variant === "hero";

  const [publishedBanner, setPublishedBanner] = useState<Banner | null>(null);
  const [previewBanner, setPreviewBanner] = useState<Banner | null>(null);
  const [isEditMode, setIsEditMode] = useState<boolean>(true);
  const [idToken, setIdToken] = useState<string | null>(cachedToken);
  const [cropImageFile, setCropImageFile] = useState<File | null>(null);
  const [showImageCropDialog, setShowImageCropDialog] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isLoadingCropSource, setIsLoadingCropSource] = useState(false);
  /** Original file from disk — kept so admin can re-open crop without re-uploading. */
  const sourceImageFileRef = useRef<File | null>(null);

  const handleBannersLoaded = (banners?: Banner[]) => {
    if (banners && banners.length > 0) {
      const preview = banners.find((banner) => banner.status === "preview") || null;
      const published = banners.find((banner) => banner.status === "published") || null;
      setPreviewBanner(preview);
      setPublishedBanner(published);
      setIsEditMode(!!preview || !published);
    } else {
      setPreviewBanner(null);
      setPublishedBanner(null);
      setIsEditMode(true);
    }
  };

  const getBanners = () => {
    axios
      .get(`${endpoints.getBanner}`)
      .then((response: AxiosResponse<Banner[]>) => {
        handleBannersLoaded(response.data);
      })
      .catch((error) => {
        console.error(error);
        Message.error(getErrorMessage(error, t("notification_messages.banner_load_error")));
      });
  };

  useEffect(() => {
    getBanners();
  }, []);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setIdToken(null);
        return;
      }
      try {
        const token = await user.getIdToken();
        setIdToken(token);
      } catch (error) {
        console.error(error);
        setIdToken(null);
      }
    });
    return unsubscribe;
  }, []);

  const uploadBannerImage = async (file: File) => {
    if (!idToken) {
      Message.warning(t("notification_messages.banner_auth_loading"));
      return;
    }

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await axios.post(endpoints.admin.uploadBanner, formData, {
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${idToken}`,
          "Content-Type": "multipart/form-data",
        },
      });
      handleBannersLoaded(response.data as Banner[]);
      invalidatePublishedBanner();
      Message.success(t("notification_messages.banner_uploaded_success"));
    } catch (error) {
      console.error(error);
      Message.error(getErrorMessage(error, t("notification_messages.banner_uploaded_error")));
    } finally {
      setIsUploading(false);
    }
  };

  const openCropDialog = (file: File) => {
    setCropImageFile(file);
    setShowImageCropDialog(true);
  };

  const beforeUpload = (file: File) => {
    if (!BANNER_IMAGE_TYPES.includes(file.type)) {
      Message.error(t("notification_messages.avatar_format_error"));
      return false;
    }
    if (!idToken) {
      Message.warning(t("notification_messages.banner_auth_loading"));
      return false;
    }
    sourceImageFileRef.current = file;
    if (useBannerCropFlow) {
      openCropDialog(file);
    } else {
      void uploadBannerImage(file);
    }
    return false;
  };

  const handleAdjustCrop = async () => {
    if (sourceImageFileRef.current) {
      openCropDialog(sourceImageFileRef.current);
      return;
    }

    const previewUrl = previewBanner?.url
      ? resolveBannerUrl(previewBanner.url)
      : publishedBanner?.url
        ? resolveBannerUrl(publishedBanner.url)
        : null;

    if (!previewUrl) {
      Message.warning(t("notification_messages.banner_upload_first"));
      return;
    }

    setIsLoadingCropSource(true);
    try {
      const file = await imageFileFromUrl(previewUrl, "banner-preview.jpg");
      sourceImageFileRef.current = file;
      openCropDialog(file);
    } catch (error) {
      console.error(error);
      Message.error(t("notification_messages.banner_uploaded_error"));
    } finally {
      setIsLoadingCropSource(false);
    }
  };

  const handleSaveBanner = () => {
    if (!previewBanner?.id) {
      Message.warning(t("notification_messages.banner_upload_first"));
      return;
    }

    axios
      .post(`${endpoints.admin.publishBanner}?id=${previewBanner.id}`)
      .then((response: AxiosResponse<Banner[]>) => {
        handleBannersLoaded(response.data);
        setIsEditMode(false);
        invalidatePublishedBanner();
        Message.success(t("notification_messages.banner_saved_success"));
      })
      .catch((error) => {
        console.error(error);
        Message.error(getErrorMessage(error, t("notification_messages.banner_saved_error")));
      });
  };

  const handleDeletePreview = () => {
    axios
      .delete(endpoints.admin.deletePreviewBanner)
      .then((response: AxiosResponse<Banner[]>) => {
        sourceImageFileRef.current = null;
        handleBannersLoaded(response.data);
        invalidatePublishedBanner();
      })
      .catch((error) => {
        console.error(error);
        Message.error(getErrorMessage(error, t("notification_messages.banner_cancel_error")));
      });
  };

  const canAdjustCrop = useBannerCropFlow && (!!previewBanner || !!publishedBanner);

  const bannerBody = isEditMode ? (
    <div className="admin-banner__upload-wrapper">
      {!idToken && (
        <p className="admin-banner__hint">{t("notification_messages.banner_auth_loading")}</p>
      )}
      <Upload
        drag
        withCredentials
        limit={1}
        accept="image/jpeg,image/png"
        disabled={!idToken || isUploading}
        showUploadList={{
          previewIcon: null,
        }}
        fileList={
          previewBanner
            ? [
                {
                  uid: previewBanner.id.toString(),
                  url: resolveBannerUrl(previewBanner.url),
                },
              ]
            : undefined
        }
        listType="picture-card"
        beforeUpload={beforeUpload}
      />
      {canAdjustCrop && (
        <Button
          className="admin-banner__adjust-crop"
          type="outline"
          shape="round"
          size={isMobile ? "small" : "large"}
          loading={isLoadingCropSource}
          disabled={!idToken || isUploading}
          onClick={handleAdjustCrop}
        >
          {t("home_page.banner_adjust_crop_button")}
        </Button>
      )}
      <div className="admin-banner__buttons">
        <Button
          className="admin-banner__button admin-banner__button-secondary"
          shape="round"
          size={isMobile ? "small" : "large"}
          onClick={handleDeletePreview}
        >
          {t("common.cancel_button")}
        </Button>
        <Button
          className={classNames("admin-banner__button admin-banner__button-primary", {
            black: currentThemeName === "black",
          })}
          shape="round"
          size={isMobile ? "small" : "large"}
          onClick={handleSaveBanner}
          disabled={!previewBanner}
        >
          {t("common.save_button")}
        </Button>
      </div>
    </div>
  ) : (
    <div className="admin-banner__image-wrapper">
      <img className="admin-banner__image" src={resolveBannerUrl(publishedBanner?.url)} alt="banner" />
    </div>
  );

  return (
    <>
      <div
        className={classNames("admin-banner", {
          "admin-banner--legacy": variant === "legacy",
          "admin-banner--hero": variant === "hero",
        })}
      >
        <div className="admin-banner__header">
          <Button
            className={classNames(
              "admin-banner__icon-button",
              { "admin-banner__icon-button--black-theme": currentThemeName === "black" },
              { "admin-banner__icon-button--active-black": isEditMode && currentThemeName === "black" },
              { "admin-banner__icon-button--active": isEditMode && currentThemeName !== "black" }
            )}
            shape="round"
            onClick={() => setIsEditMode(true)}
          >
            <EditIcon fill={currentTheme.headerTextColor} />
          </Button>
        </div>
        {variant === "legacy" ? (
          <Card className="card-with-icon-hover own-card">{bannerBody}</Card>
        ) : (
          bannerBody
        )}
      </div>

      {useBannerCropFlow && cropImageFile && (
        <EventImageCropDialog
          visible={showImageCropDialog}
          file={cropImageFile}
          aspect={BANNER_CROP_ASPECT}
          titleKey="home_page.banner_image_crop_title"
          helpKey="home_page.banner_image_crop_help"
          confirmKey="home_page.banner_image_crop_confirm"
          onCancel={() => {
            setShowImageCropDialog(false);
            setCropImageFile(null);
          }}
          onConfirm={async (file) => {
            setShowImageCropDialog(false);
            setCropImageFile(null);
            await uploadBannerImage(file);
          }}
        />
      )}
    </>
  );
}
